import ListPagination from '../components/common/ListPagination';
import useListPagination from '../hooks/useListPagination';
import useLatestRequest from '../hooks/useLatestRequest';
import RefreshRegion from '../components/common/RefreshRegion';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ReceiptRefundIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Modal from '../components/ui/Modal';
import Dropdown from '../components/ui/Dropdown';
import EmployeeAvatar from '../components/common/EmployeeAvatar';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import ClaimsService from '../services/claims.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const getClaimLifecycleStatus = (claim) => {
  if (!claim) return 'PENDING';

  const claimStatus = claim.claimStatus ? String(claim.claimStatus).toUpperCase() : null;
  const managerStatus = claim.managerApprovalStatus
    ? String(claim.managerApprovalStatus).toUpperCase()
    : null;

  if (claimStatus && claimStatus !== 'DRAFT') {
    return claimStatus;
  }

  if (managerStatus === 'APPROVED') return 'PROCESSING';
  if (managerStatus === 'REJECTED') return 'REJECTED';
  if (managerStatus === 'PENDING') return 'SUBMITTED';

  const legacy = claim.status ? String(claim.status).toUpperCase() : 'PENDING';
  if (legacy === 'APPROVED') return 'PROCESSING';
  return legacy;
};

const formatClaimStatusLabel = (status) => {
  const normalized = String(status || '').toUpperCase();
  const labels = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    PROCESSING: 'Processing',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    PAID: 'Refund completed',
    PENDING: 'Pending',
  };
  if (labels[normalized]) return labels[normalized];
  const lower = String(status || 'pending').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const getPaymentReceiptUrl = (claim) => {
  if (!claim) return '';
  if (claim.paymentReceiptUrl) return claim.paymentReceiptUrl;

  const markerPrefix = 'PAYMENT_RECEIPT_URL:';
  const comments = String(claim.comments || '');
  const markerLine = comments
    .split('\n')
    .find((line) => line.trim().startsWith(markerPrefix));

  if (!markerLine) return '';
  return markerLine.trim().slice(markerPrefix.length).trim();
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CLAIM_APPROVER_EMAILS = String(
  process.env.REACT_APP_CLAIM_APPROVER_EMAILS || ''
)
  .split(',')
  .map((email) => String(email || '').trim().toLowerCase())
  .filter(Boolean);
const Claims = () => {
  const { user, isAccountant } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const userEmail = String(user?.email || user?.email_address || '').toLowerCase();
  const canApproveClaims = CLAIM_APPROVER_EMAILS.includes(userEmail);
  const canManageRefunds = isAccountant;
  const today = new Date();
  const earliestClaimDate = new Date(today);
  earliestClaimDate.setDate(today.getDate() - 29);
  const minimumExpenseDate = formatDateInputValue(earliestClaimDate);
  const maximumExpenseDate = formatDateInputValue(today);
  const [activeTab, setActiveTab] = useState('my-claims');
  const [claims, setClaims] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvedClaims, setApprovedClaims] = useState([]);
  const [processingClaims, setProcessingClaims] = useState([]);
  const [completedRefundClaims, setCompletedRefundClaims] = useState([]);
  const { loading, begin: beginFetchClaims } = useLatestRequest();
  const { loading: approvalsLoading, begin: beginFetchPendingApprovals } = useLatestRequest();
  const { loading: approvedLoading, begin: beginFetchApprovedClaims } = useLatestRequest();
  const { loading: financeLoading, begin: beginFetchProcessingClaims } = useLatestRequest();
  const { loading: completedRefundsLoading, begin: beginFetchCompletedRefundClaims } = useLatestRequest();
  const [showForm, setShowForm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [paymentReceiptFiles, setPaymentReceiptFiles] = useState({});
  const [filter, setFilter] = useState('all');

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const [formData, setFormData] = useState({
    type: '',
    amount: '',
    description: '',
    date: '',
    receipt: null
  });

  useEffect(() => {
    if (activeTab === 'my-claims') {
      fetchClaims();
    } else if (activeTab === 'approvals' && canApproveClaims) {
      fetchPendingApprovals();
    } else if (activeTab === 'approved' && canApproveClaims) {
      fetchApprovedClaims();
    } else if (activeTab === 'finance' && canManageRefunds) {
      fetchProcessingClaims();
    } else if (activeTab === 'completed-refunds' && canManageRefunds) {
      fetchCompletedRefundClaims();
    }
  }, [activeTab, filter, canApproveClaims, canManageRefunds]);

  const fetchClaims = async () => {
    const request = beginFetchClaims();
    try {
      const response = await ClaimsService.getClaims({ status: filter });
      if (!request.isCurrent()) return;
      setClaims(response.claims || []);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching claims:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load claims';
      toast.error(errorMessage);
      setClaims([]);
    } finally {
      request.finish();
    }
  };

  const fetchPendingApprovals = async () => {
    const request = beginFetchPendingApprovals();
    try {
      const response = await ClaimsService.getClaimsByManager();
      if (!request.isCurrent()) return;
      setPendingApprovals(response.claims || []);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching pending approvals:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load pending approvals';
      toast.error(errorMessage);
      setPendingApprovals([]);
    } finally {
      request.finish();
    }
  };

  const fetchApprovedClaims = async () => {
    const request = beginFetchApprovedClaims();
    try {
      const response = await ClaimsService.getApprovedClaimsByManager();
      if (!request.isCurrent()) return;
      setApprovedClaims(response.claims || []);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching approved claims:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load approved claims';
      toast.error(errorMessage);
      setApprovedClaims([]);
    } finally {
      request.finish();
    }
  };

  const fetchProcessingClaims = async () => {
    const request = beginFetchProcessingClaims();
    try {
      const response = await ClaimsService.getProcessingClaims();
      if (!request.isCurrent()) return;
      setProcessingClaims(response.claims || []);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching processing claims:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load claims awaiting payment';
      toast.error(errorMessage);
      setProcessingClaims([]);
    } finally {
      request.finish();
    }
  };

  const fetchCompletedRefundClaims = async () => {
    const request = beginFetchCompletedRefundClaims();
    try {
      const response = await ClaimsService.getCompletedRefundClaims();
      if (!request.isCurrent()) return;
      setCompletedRefundClaims(response.claims || []);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching completed refunds:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to load completed refunds';
      toast.error(errorMessage);
      setCompletedRefundClaims([]);
    } finally {
      request.finish();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.date < minimumExpenseDate || formData.date > maximumExpenseDate) {
      toast.error('Expense date must be within the last 30 days, including today');
      return;
    }

    if (!formData.receipt) {
      toast.error('Please upload an expense receipt');
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await ClaimsService.createClaimWithFile(formDataToSend);

      toast.success('Claim submitted successfully');
      setShowForm(false);
      setFormData({
        type: '',
        amount: '',
        description: '',
        date: '',
        receipt: null
      });
      fetchClaims();
    } catch (error) {
      console.error('Error submitting claim:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to submit claim';
      toast.error(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleApproveClaim = async (claimId) => {
    if (!canApproveClaims) {
      toast.error('Only designated approver can approve claims');
      return;
    }
    try {
      await ClaimsService.updateClaimStatus(claimId, {
        managerApprovalStatus: 'APPROVED'
      });
      toast.success('Claim approved — sent to finance for processing');
      fetchPendingApprovals();
      fetchApprovedClaims();
      if (canManageRefunds) {
        fetchProcessingClaims();
      }
    } catch (error) {
      console.error('Error approving claim:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to approve claim';
      toast.error(errorMessage);
    }
  };

  const handleRejectClaim = async () => {
    if (!canApproveClaims) {
      toast.error('Only designated approver can reject claims');
      return;
    }
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await ClaimsService.updateClaimStatus(selectedClaim.id, {
        managerApprovalStatus: 'REJECTED',
        rejectionReason: rejectionReason.trim()
      });
      toast.success('Claim rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedClaim(null);
      fetchPendingApprovals();
      fetchClaims();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to reject claim';
      toast.error(errorMessage);
    }
  };

  const handlePaymentReceiptChange = (claimId, file) => {
    setPaymentReceiptFiles((prev) => ({
      ...prev,
      [claimId]: file || null,
    }));
  };

  const handleViewReceipt = async (claimId, paymentReceipt = false) => {
    try {
      const url = await ClaimsService.getReceiptViewUrl(claimId, paymentReceipt);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.message || 'Failed to open receipt');
    }
  };

  const handleMarkPaid = async (claimId) => {
    if (!canManageRefunds) {
      toast.error('Only accountants can mark refunds as completed');
      return;
    }

    const paymentReceipt = paymentReceiptFiles[claimId];
    if (!paymentReceipt) {
      toast.error('Please upload payment receipt before completing refund');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('paymentReceipt', paymentReceipt);

      await ClaimsService.markClaimPaid(claimId, formData);
      toast.success('Refund marked as completed');
      setPaymentReceiptFiles((prev) => {
        const next = { ...prev };
        delete next[claimId];
        return next;
      });
      fetchProcessingClaims();
      fetchCompletedRefundClaims();
      fetchClaims();
    } catch (error) {
      console.error('Error marking claim paid:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to mark payment complete';
      toast.error(errorMessage);
    }
  };

  // Filter functions
  const filterClaims = (claimsToFilter) => {
    return claimsToFilter.filter(claim => {
      // Status filter
      const claimStatus = getClaimLifecycleStatus(claim);
      if (statusFilter !== 'all' && claimStatus !== statusFilter) {
        return false;
      }

      // Search filter (combine local and header search)
      const combinedSearch = headerSearch || searchQuery;
      if (combinedSearch) {
        const searchLower = combinedSearch.toLowerCase();
        const matchesSearch =
          (claim.employeeName && claim.employeeName.toLowerCase().includes(searchLower)) ||
          ((claim.expenseItem || claim.type) && (claim.expenseItem || claim.type).toLowerCase().includes(searchLower)) ||
          ((claim.justification || claim.description) && (claim.justification || claim.description).toLowerCase().includes(searchLower)) ||
          ((claim.employeeId || claim.employeeEmail) && (claim.employeeId || claim.employeeEmail).toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;
      }

      // Date range filter
      const claimDate = claim.expenseDate || claim.date;
      if (dateRange.startDate && claimDate < dateRange.startDate) {
        return false;
      }
      if (dateRange.endDate && claimDate > dateRange.endDate) {
        return false;
      }

      return true;
    });
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setDateRange({ startDate: '', endDate: '' });
  };

  // Status filter options
  const statusOptions = [
    { value: 'all', label: 'All Status', icon: DocumentTextIcon },
    { value: 'DRAFT', label: 'Draft', icon: DocumentTextIcon },
    { value: 'SUBMITTED', label: 'Submitted', icon: ClockIcon },
    { value: 'PROCESSING', label: 'Processing', icon: CurrencyDollarIcon },
    { value: 'APPROVED', label: 'Approved', icon: CheckCircleIcon },
    { value: 'REJECTED', label: 'Rejected', icon: XCircleIcon },
    { value: 'PAID', label: 'Refund completed', icon: CurrencyDollarIcon }
  ];

  const getStatusColor = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    switch (normalizedStatus) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'processing': return 'text-indigo-600 bg-indigo-100';
      case 'submitted': return 'text-blue-600 bg-blue-100';
      case 'paid': return 'text-purple-600 bg-purple-100';
      case 'draft':
      case 'pending':
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getStatusIcon = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    switch (normalizedStatus) {
      case 'approved': return CheckCircleIcon;
      case 'rejected': return XCircleIcon;
      case 'processing': return CurrencyDollarIcon;
      case 'submitted': return ClockIcon;
      case 'paid': return CheckCircleIcon;
      case 'draft':
      case 'pending':
      default: return ClockIcon;
    }
  };

  const claimTypes = [
    { value: 'Travel', label: 'Travel', icon: CalendarIcon },
    { value: 'Meal', label: 'Meal', icon: CreditCardIcon },
    { value: 'Internet', label: 'Internet & Phone', icon: CreditCardIcon },
    { value: 'Medical', label: 'Medical', icon: CreditCardIcon },
    { value: 'Office Supplies', label: 'Office Supplies', icon: DocumentTextIcon },
    { value: 'Training', label: 'Training', icon: UserIcon },
    { value: 'Other', label: 'Other', icon: DocumentTextIcon }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Claims' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'PROCESSING', label: 'Processing' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'PAID', label: 'Refund completed' }
  ];

  const displayClaims = claims;

  // Finance payment card (accountant)
  const FinancePaymentCard = ({ claim, index }) => (
    <motion.div
      key={claim.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
    >
      <Card className="hover:shadow-lg transition-shadow duration-300">
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <EmployeeAvatar
                record={claim}
                name={claim.employeeName}
                email={claim.employeeId || claim.employeeEmail}
                sizeClass="w-12 h-12"
              />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{claim.employeeName}</h3>
                <p className="text-sm text-gray-600">{claim.employeeId || claim.employeeEmail}</p>
              </div>
            </div>
            <span className="px-3 py-1 text-sm font-medium rounded-full text-indigo-800 bg-indigo-100">
              Processing
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <CreditCardIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Claim Type</p>
                <p className="font-medium">{claim.expenseItem || claim.type}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="font-medium text-lg">${claim.expenseAmount || claim.amount}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="font-medium">
                  {claim.approvedAt
                    ? formatDateDisplay(claim.approvedAt)
                    : '—'}
                </p>
              </div>
            </div>
          </div>

          {(claim.receiptUrl || claim.receipt) && (
            <div className="mt-4 flex items-center space-x-2">
              <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
              <button
                type="button"
                onClick={() => handleViewReceipt(claim.id)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View Receipt
              </button>
            </div>
          )}

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload payment receipt *
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handlePaymentReceiptChange(claim.id, e.target.files?.[0])}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            {paymentReceiptFiles[claim.id] && (
              <p className="mt-2 text-sm text-green-700">
                Selected: {paymentReceiptFiles[claim.id].name}
              </p>
            )}
          </div>

          <div className="mt-6">
            <Button
              variant="success"
              onClick={() => handleMarkPaid(claim.id)}
              className="flex items-center space-x-2"
            >
              <CheckCircleIcon className="w-4 h-4" />
              <span>Mark refund completed</span>
            </Button>
          </div>
        </Card.Body>
      </Card>
    </motion.div>
  );

  // Approval Card Component
  const ApprovalCard = ({ claim, index }) => {
    const StatusIcon = getStatusIcon(getClaimLifecycleStatus(claim));

    return (
      <motion.div
        key={claim.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
      >
        <Card className="hover:shadow-lg transition-shadow duration-300">
          <Card.Body className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <EmployeeAvatar
                  record={claim}
                  name={claim.employeeName}
                  email={claim.employeeId || claim.employeeEmail}
                  sizeClass="w-12 h-12"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{claim.employeeName}</h3>
                  <p className="text-sm text-gray-600">{claim.employeeId || claim.employeeEmail}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
                  Pending Approval
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <CreditCardIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Claim Type</p>
                  <p className="font-medium">{claim.expenseItem || claim.type}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <CurrencyDollarIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="font-medium text-lg">${claim.expenseAmount || claim.amount}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Expense Date</p>
                  <p className="font-medium">{formatDateDisplay(claim.expenseDate || claim.date)}</p>
                </div>
              </div>
            </div>

            {claim.description && (
              <div className="mt-4 flex items-start space-x-2">
                <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Description</p>
                  <p className="text-gray-900">{claim.justification || claim.description}</p>
                </div>
              </div>
            )}

            {(claim.receiptUrl || claim.receipt) && (
              <div className="mt-4 flex items-center space-x-2">
                <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                <button
                  type="button"
                  onClick={() => handleViewReceipt(claim.id)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Receipt
                </button>
              </div>
            )}

            <div className="mt-6 flex space-x-3">
              <Button
                variant="success"
                onClick={() => handleApproveClaim(claim.id)}
                className="flex items-center space-x-2"
              >
                <CheckCircleIcon className="w-4 h-4" />
                <span>Approve</span>
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setSelectedClaim(claim);
                  setShowRejectModal(true);
                }}
                className="flex items-center space-x-2"
              >
                <XCircleIcon className="w-4 h-4" />
                <span>Reject</span>
              </Button>
            </div>
          </Card.Body>
        </Card>
      </motion.div>
    );
  };

  const tabLoading =
    (activeTab === 'my-claims' && loading) ||
    (activeTab === 'approvals' && approvalsLoading) ||
    (activeTab === 'approved' && approvedLoading) ||
    (activeTab === 'finance' && financeLoading) ||
    (activeTab === 'completed-refunds' && completedRefundsLoading);
  const activeClaims = activeTab === 'finance' ? processingClaims
    : activeTab === 'completed-refunds' ? completedRefundClaims
    : activeTab === 'approved' ? approvedClaims
    : activeTab === 'my-claims' ? displayClaims : pendingApprovals;
  const pagination = useListPagination(filterClaims(activeClaims), JSON.stringify([activeTab, filter, statusFilter, headerSearch, searchQuery, dateRange]));


  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Claims Management</h1>
            <p className="text-purple-100">Submit and track your expense claims</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-white bg-opacity-20 text-white border-2 border-white hover:bg-white hover:text-purple-600 transition-all duration-200"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            New Claim
          </Button>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <Button
                variant={activeTab === 'my-claims' ? 'primary' : 'outline'}
                onClick={() => setActiveTab('my-claims')}
                className="flex items-center space-x-2"
              >
                <CreditCardIcon className="w-4 h-4" />
                <span>My Claims</span>
                {claims && claims.length > 0 && (
                  <span className={`px-2 py-1 text-xs rounded-full ${activeTab === 'my-claims' ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                    {claims.length}
                  </span>
                )}
              </Button>

              {canApproveClaims && (
                <Button
                  variant={activeTab === 'approvals' ? 'primary' : 'outline'}
                  onClick={() => setActiveTab('approvals')}
                  className="flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Pending Approvals</span>
                  {pendingApprovals && pendingApprovals.length > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${activeTab === 'approvals' ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {pendingApprovals.length}
                    </span>
                  )}
                </Button>
              )}

              {canApproveClaims && (
                <Button
                  variant={activeTab === 'approved' ? 'primary' : 'outline'}
                  onClick={() => setActiveTab('approved')}
                  className="flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Approved</span>
                  {approvedClaims && approvedClaims.length > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${activeTab === 'approved' ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {approvedClaims.length}
                    </span>
                  )}
                </Button>
              )}

              {canManageRefunds && (
                <Button
                  variant={activeTab === 'finance' ? 'primary' : 'outline'}
                  onClick={() => setActiveTab('finance')}
                  className="flex items-center space-x-2"
                >
                  <CurrencyDollarIcon className="w-4 h-4" />
                  <span>Finance — Refunds</span>
                  {processingClaims && processingClaims.length > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${activeTab === 'finance' ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {processingClaims.length}
                    </span>
                  )}
                </Button>
              )}

              {canManageRefunds && (
                <Button
                  variant={activeTab === 'completed-refunds' ? 'primary' : 'outline'}
                  onClick={() => setActiveTab('completed-refunds')}
                  className="flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Completed Refunds</span>
                  {completedRefundClaims && completedRefundClaims.length > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${activeTab === 'completed-refunds' ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {completedRefundClaims.length}
                    </span>
                  )}
                </Button>
              )}
            </div>

          </div>
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card>
        <Card.Body className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">Filters</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="text-xs px-2 py-1"
            >
              Clear
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Status Filter */}
            <div>
              <Dropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
                placeholder="Status"
                size="sm"
                className="w-full"
              />
            </div>

            {/* Search */}
            {/* <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div> */}

            {/* Start Date */}
            <div>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                placeholder="From Date"
              />
            </div>

            {/* End Date */}
            <div>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                placeholder="To Date"
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Claims List */}
      <RefreshRegion loading={tabLoading}>
      <div ref={pagination.topRef}><ListPagination {...pagination} compact /></div>
      <div className="space-y-4">
        {activeTab === 'finance' && canManageRefunds ? (
          (() => {
            const filteredFinance = filterClaims(processingClaims);
            return filteredFinance.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Matching {filteredFinance.length} of {processingClaims.length} claims awaiting refund
                  </p>
                </div>
                {pagination.items.map((claim, index) => (
                  <FinancePaymentCard key={claim.id} claim={claim} index={index} />
                ))}
              </>
            ) : processingClaims.length > 0 ? (
              <Card>
                <Card.Body className="p-12 text-center">
                  <FunnelIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No claims match your filters</h3>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <CurrencyDollarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No refunds pending</h3>
                  <p className="text-gray-600">Approved claims awaiting payment will appear here.</p>
                </Card.Body>
              </Card>
            );
          })()
        ) : activeTab === 'completed-refunds' && canManageRefunds ? (
          (() => {
            const filteredCompleted = filterClaims(completedRefundClaims);
            return filteredCompleted.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Matching {filteredCompleted.length} of {completedRefundClaims.length} completed refunds
                  </p>
                </div>
                {pagination.items.map((claim, index) => {
                  const lifecycleStatus = getClaimLifecycleStatus(claim);
                  const StatusIcon = getStatusIcon(lifecycleStatus);
                  return (
                    <motion.div
                      key={claim.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Card className="hover:shadow-lg transition-shadow duration-300">
                        <Card.Body className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <EmployeeAvatar
                                record={claim}
                                name={claim.employeeName}
                                email={claim.employeeId || claim.employeeEmail}
                                sizeClass="w-12 h-12"
                              />
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{claim.employeeName}</h3>
                                <p className="text-sm text-gray-600">{claim.employeeId || claim.employeeEmail}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2 mb-2">
                                <StatusIcon className="w-5 h-5" />
                                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(lifecycleStatus)}`}>
                                  {formatClaimStatusLabel(lifecycleStatus)}
                                </span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">${claim.expenseAmount || claim.amount}</p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center space-x-2">
                              <CreditCardIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Claim Type</p>
                                <p className="font-medium">{claim.expenseItem || claim.type}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Paid Date</p>
                                <p className="font-medium">
                                  {(claim.paidAt || claim.updatedAt)
                                    ? formatDateDisplay(claim.paidAt || claim.updatedAt)
                                    : '—'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {getPaymentReceiptUrl(claim) && (
                            <div className="mt-4 flex items-center space-x-2">
                              <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                              <button
                                type="button"
                                onClick={() => handleViewReceipt(claim.id, true)}
                                className="text-green-700 hover:text-green-800 text-sm font-medium"
                              >
                                View Payment Receipt
                              </button>
                            </div>
                          )}
                          {(claim.receiptUrl || claim.receipt) && (
                            <div className="mt-3 flex items-center space-x-2">
                              <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                              <button type="button" onClick={() => handleViewReceipt(claim.id)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                                View Expense Receipt
                              </button>
                            </div>
                          )}
                          {claim.paidBy && (
                            <p className="mt-3 text-sm text-gray-600">Marked paid by: <span className="font-medium text-gray-900">{claim.paidByName || claim.paidBy}</span></p>
                          )}
                        </Card.Body>
                      </Card>
                    </motion.div>
                  );
                })}
              </>
            ) : completedRefundClaims.length > 0 ? (
              <Card>
                <Card.Body className="p-12 text-center">
                  <FunnelIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No completed refunds match your filters</h3>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <CheckCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No completed refunds yet</h3>
                  <p className="text-gray-600">Completed finance refunds will appear here.</p>
                </Card.Body>
              </Card>
            );
          })()
        ) : activeTab === 'approved' && canApproveClaims ? (
          (() => {
            const filteredApproved = filterClaims(approvedClaims);
            return filteredApproved.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Matching {filteredApproved.length} of {approvedClaims.length} approved claims
                  </p>
                </div>
                {pagination.items.map((claim, index) => {
                  const lifecycleStatus = getClaimLifecycleStatus(claim);
                  const StatusIcon = getStatusIcon(lifecycleStatus);
                  return (
                    <motion.div
                      key={claim.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Card className="hover:shadow-lg transition-shadow duration-300">
                        <Card.Body className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <EmployeeAvatar
                                record={claim}
                                name={claim.employeeName}
                                email={claim.employeeId || claim.employeeEmail}
                                sizeClass="w-12 h-12"
                              />
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{claim.employeeName}</h3>
                                <p className="text-sm text-gray-600">{claim.employeeId || claim.employeeEmail}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2 mb-2">
                                <StatusIcon className="w-5 h-5" />
                                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(lifecycleStatus)}`}>
                                  {formatClaimStatusLabel(lifecycleStatus)}
                                </span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">${claim.expenseAmount || claim.amount}</p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center space-x-2">
                              <CreditCardIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Claim Type</p>
                                <p className="font-medium">{claim.expenseItem || claim.type}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Approved Date</p>
                                <p className="font-medium">
                                  {claim.approvedAt ? formatDateDisplay(claim.approvedAt) : '—'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Submitted</p>
                                <p className="font-medium">{formatDateDisplay(claim.submissionDate || claim.submittedDate)}</p>
                              </div>
                            </div>
                            {(claim.receiptUrl || claim.receipt) && (
                              <div className="mt-4 flex items-center space-x-2">
                                <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                                <button type="button" onClick={() => handleViewReceipt(claim.id)}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                                  View expense receipt
                                </button>
                              </div>
                            )}
                            {getPaymentReceiptUrl(claim) && (
                              <div className="mt-4 flex items-center space-x-2">
                                <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                                <button type="button" onClick={() => handleViewReceipt(claim.id, true)}
                                  className="text-green-700 hover:text-green-800 text-sm font-medium">
                                  View payment slip
                                </button>
                              </div>
                            )}
                            {claim.paidBy && (
                              <p className="mt-3 text-sm text-gray-600">Marked paid by: <span className="font-medium text-gray-900">{claim.paidByName || claim.paidBy}</span></p>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </motion.div>
                  );
                })}
              </>
            ) : approvedClaims.length > 0 ? (
              <Card>
                <Card.Body className="p-12 text-center">
                  <FunnelIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No approved claims match your filters</h3>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <CheckCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No approved claims yet</h3>
                  <p className="text-gray-600">Approved claims will appear here once processed by approver.</p>
                </Card.Body>
              </Card>
            );
          })()
        ) : activeTab === 'my-claims' ? (
          (() => {
            const filteredClaims = filterClaims(displayClaims);
            return filteredClaims.length > 0 ? (
              <>
                {/* Results count */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Matching {filteredClaims.length} of {displayClaims.length} claims
                  </p>
                </div>
                {pagination.items.map((claim, index) => {
                  const lifecycleStatus = getClaimLifecycleStatus(claim);
                  const StatusIcon = getStatusIcon(lifecycleStatus);
                  return (
                    <motion.div
                      key={claim.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Card className="hover:shadow-lg transition-shadow duration-300">
                        <Card.Body className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <CreditCardIcon className="w-6 h-6 text-purple-600" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{claim.expenseItem || claim.type}</h3>
                                <p className="text-sm text-gray-600">{claim.justification || claim.description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2 mb-2">
                                <StatusIcon className="w-5 h-5" />
                                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(lifecycleStatus)}`}>
                                  {formatClaimStatusLabel(lifecycleStatus)}
                                </span>
                              </div>
                              <p className="text-2xl font-bold text-gray-900">${claim.expenseAmount || claim.amount}</p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Expense Date</p>
                                <p className="font-medium">{formatDateDisplay(claim.expenseDate || claim.date)}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-600">Submitted</p>
                                <p className="font-medium">{formatDateDisplay(claim.submissionDate || claim.submittedDate)}</p>
                              </div>
                            </div>
                          </div>

                          {getPaymentReceiptUrl(claim) && (
                            <div className="mt-4 flex items-center space-x-2">
                              <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                              <button
                                type="button"
                                onClick={() => handleViewReceipt(claim.id, true)}
                                className="text-green-700 hover:text-green-800 text-sm font-medium"
                              >
                                View Payment Receipt
                              </button>
                            </div>
                          )}
                          {(claim.receiptUrl || claim.receipt) && (
                            <div className="mt-3 flex items-center space-x-2">
                              <ReceiptRefundIcon className="w-5 h-5 text-gray-400" />
                              <button
                                type="button"
                                onClick={() => handleViewReceipt(claim.id)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                View Expense Receipt
                              </button>
                            </div>
                          )}
                          {claim.paidBy && (
                            <p className="mt-3 text-sm text-gray-600">Marked paid by: <span className="font-medium text-gray-900">{claim.paidByName || claim.paidBy}</span></p>
                          )}
                        </Card.Body>
                      </Card>
                    </motion.div>
                  );
                })}
              </>
            ) : displayClaims.length > 0 ? (
              <Card>
                <Card.Body className="p-12 text-center">
                  <FunnelIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No claims match your filters</h3>
                  <p className="text-gray-600 mb-4">Try adjusting your search criteria or clear all filters.</p>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <CreditCardIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Claims Found</h3>
                  <p className="text-gray-600">You haven't submitted any claims yet.</p>
                </Card.Body>
              </Card>
            );
          })()
        ) : (
          (() => {
            const filteredApprovals = filterClaims(pendingApprovals);
            return filteredApprovals.length > 0 ? (
              <>
                {/* Results count */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Matching {filteredApprovals.length} of {pendingApprovals.length} pending claims
                  </p>
                </div>
                {pagination.items.map((claim, index) => (
                  <ApprovalCard key={claim.id} claim={claim} index={index} />
                ))}
              </>
            ) : pendingApprovals.length > 0 ? (
              <Card>
                <Card.Body className="p-12 text-center">
                  <FunnelIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No claims match your filters</h3>
                  <p className="text-gray-600 mb-4">Try adjusting your search criteria or clear all filters.</p>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <CheckCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Claims</h3>
                  <p className="text-gray-600">All claims have been processed.</p>
                </Card.Body>
              </Card>
            );
          })()
        )}
      </div>
      <ListPagination {...pagination} label="claims" disabled={tabLoading} />
      </RefreshRegion>

      {/* Claim Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={() => setShowForm(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Submit New Claim</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Type
                </label>
                <Dropdown
                  value={formData.type}
                  onChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                  options={claimTypes}
                  placeholder="Select claim type"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ($)
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0.00"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows="3"
                  placeholder="Describe your expense..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  min={minimumExpenseDate}
                  max={maximumExpenseDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt *
                </label>
                <input
                  type="file"
                  name="receipt"
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  accept="image/*,.pdf"
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button type="submit" className="flex-1">
                  Submit Claim
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Rejection Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectionReason('');
          setSelectedClaim(null);
        }}
        title="Reject Claim Request"
        size="md"
      >
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Claim</h3>
            <p className="text-sm text-gray-600">
              Please provide a clear reason for rejecting this claim request.
            </p>
          </div>

          {selectedClaim && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Claim Details</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="font-medium">Employee:</span> {selectedClaim.employeeName}</p>
                <p><span className="font-medium">Type:</span> {selectedClaim.expenseItem || selectedClaim.type}</p>
                <p><span className="font-medium">Amount:</span> ${selectedClaim.expenseAmount || selectedClaim.amount}</p>
                <p><span className="font-medium">Date:</span> {formatDateDisplay(selectedClaim.expenseDate || selectedClaim.date)}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Please provide a detailed reason for rejection (e.g., insufficient documentation, policy violation, etc.)"
                required
              />
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex">
                <XCircleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-red-800">Rejection Notice</h4>
                  <p className="text-sm text-red-700 mt-1">
                    The employee will be notified via email about this rejection and the reason provided.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedClaim(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectClaim}
                variant="danger"
                icon={XCircleIcon}
              >
                Reject Claim
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Claims;
